import React, {useState} from 'react';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {usePollingAnalysis} from 'analysis-tool/polling/usePollingPipeline';
import {DataReference} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  MODAL_SUPPORTED_STEP_CREATED_EVENT,
  MODAL_SUPPORTED_STEP_STARTED_EVENT,
} from 'performance/processAnalysesEvents';
import {
  isStepInputParentsComplete,
  StepInputParentsConfigurationProps,
} from 'pipelines/generalized-step-config-editor/StepInputParentsConfiguration';
import InterpolationStepInputConfiguration from 'pipelines/interpolation/InterpolationStepInputConfiguration';
import {DraftStepInputParents} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {
  getSingleDataFrameInputStepDataName,
  getSingleDataFrameOutputStepDataName,
} from 'pipelines/StepDataUtils';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import {JSONValue} from 'util/Types';

import StepConfigEditorModal, {StepConfigModalProps} from './StepConfigEditorModal';

/** @package */
export const OUTDATED_ANALYSIS_ERROR =
  'This analysis has been modified since you started configuring this transformation. Please copy your changes, close this modal, and try again.';

function getStepInputParentsFromStep(
  analysis: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): DraftStepInputParents {
  const incomingEdges = step.definition.incomingEdges;

  let stepInputParents: DraftStepInputParents = {};

  for (const edge of incomingEdges) {
    const stepDataName = edge.childStepData.name;
    const parentInformation: DataReference = {
      stepId: edge.parent.step.id,
      stepDataName: edge.parentStepData.name,
    };

    stepInputParents = {
      ...stepInputParents,
      [stepDataName]: [...(stepInputParents[stepDataName] ?? []), parentInformation],
    };
  }

  return stepInputParents;
}

function renderStepInputComponent(
  stepType: PipelineStepType,
  stepVersion: number,
  props: StepInputParentsConfigurationProps
): JSX.Element {
  if (stepType === PipelineStepType.REGRESSION_INTERPOLATION && stepVersion === 0) {
    return <InterpolationStepInputConfiguration {...props} />;
  } else {
    return <></>;
  }
}

export type BaseInputAwareStepConfigEditorModalProps = {
  /** Called on the modal's onRequestSubmit in order to define stepInputParents dynamically. */
  handleRequestSubmit: (
    config: Record<string, JSONValue>,
    stepInputParents: DraftStepInputParents | null
  ) => Promise<GetAnalysisPipelineStep>;
} & Omit<StepConfigModalProps, 'renderStepInputComponent'>;

// Need to define `analysis`/`analysisId` on the specific components because the tagged union here trips up
// TypeScript's type resolution when using utility types on this type. It combines the `step` and `precedingStep`
// into `GetAnalysisPipelineStep | null` types props.
type InputAwareStepConfigEditorModalPropsWithoutAnalysis =
  | (BaseInputAwareStepConfigEditorModalProps & {
      /**
       * Step whose SINGLE_DATASET_OUTPUT_STEP_DATA_NAME step data is the input to the new transform step.
       * Defined if the caller is creating a new step rather than updating the step.
       * */
      precedingStep: GetAnalysisPipelineStep;
      step: null;
    })
  | (BaseInputAwareStepConfigEditorModalProps & {
      /** Defined if the caller is updating the step rather than creating a new step. */
      step: GetAnalysisPipelineStep;
      precedingStep: null;
    });

/** @package */
export function InputAwareStepConfigEditorModal({
  analysis,
  handleRequestSubmit,
  // `onRequestSubmit` must be defined and called in order for `legacyRunModal` to work with this component correctly
  onRequestSubmit = () => {},
  precedingStep,
  step,
  ...configProps
}: InputAwareStepConfigEditorModalPropsWithoutAnalysis & {analysis: PipelineGraphQL}): JSX.Element {
  const [originalModifiedAt, _] = useState<string>(analysis.modifiedAt);
  const precedingStepDatasetOutputStepDataName =
    precedingStep != null
      ? getSingleDataFrameOutputStepDataName({
          stepType: precedingStep.definition.stepType,
          stepVersion: precedingStep.definition.stepVersion,
        })
      : getSingleDataFrameInputStepDataName({
          stepType: step.definition.stepType,
          stepVersion: step.definition.stepVersion,
        });
  const [stepInputParents, setStepInputParents] = useState<DraftStepInputParents>({
    ...(precedingStep == null
      ? {}
      : {
          [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
            {
              stepId: precedingStep.id,
              stepDataName: precedingStepDatasetOutputStepDataName,
            },
          ],
        }),
    ...(step == null ? {} : getStepInputParentsFromStep(analysis, step)),
  });

  const isOutdated = analysis.modifiedAt !== originalModifiedAt;
  return (
    <StepConfigEditorModal
      {...configProps}
      onRequestSubmit={async (config) => {
        logProfilerTimingEvent(MODAL_SUPPORTED_STEP_STARTED_EVENT);
        const modalStep = await handleRequestSubmit(config, stepInputParents);
        logProfilerTimingEvent(MODAL_SUPPORTED_STEP_CREATED_EVENT, {stepId: modalStep.id});
        onRequestSubmit(config);
      }}
      renderStepInputComponent={(config, setConfig) =>
        renderStepInputComponent(configProps.stepType, configProps.stepVersion, {
          analysis,
          config,
          setConfig,
          stepInputParents,
          setStepInputParents,
          referenceStep: step ?? precedingStep,
        })
      }
      inputIsComplete={isStepInputParentsComplete(
        stepInputParents,
        configProps.stepType,
        configProps.stepVersion
      )}
      error={isOutdated ? OUTDATED_ANALYSIS_ERROR : undefined}
      readOnly={isOutdated}
      clientStateId={analysis.id}
      step={step}
    />
  );
}

export default function QueryInputAwareStepConfigEditorModal({
  analysisId,
  ...props
}: InputAwareStepConfigEditorModalPropsWithoutAnalysis & {analysisId: string}): JSX.Element {
  assert(
    (props.step != null && props.precedingStep == null) ||
      (props.step == null && props.precedingStep != null),
    'Either step or precedingStep must be defined'
  );
  const result = usePollingAnalysis(analysisId);

  if (result.type !== QueryResultType.SUCCESS) {
    return (
      <Modal
        onRequestSubmit={() => {}}
        onRequestClose={() => {}}
        buttons={[
          {
            submit: true,
            text: 'Submit',
            disabled: true,
          },
        ]}
      >
        <CenteredHorizontalThrobber />
      </Modal>
    );
  }

  assert(!isInaccessible(result.data.analysis), 'Expeceted analysis to be accessible');

  // We want to query for the actual analysis here instead of passing it in case it gets updated while the modal is open. Otherwise, we might end up creating
  // the step from outdated analysis data.
  return <InputAwareStepConfigEditorModal {...props} analysis={result.data.analysis} />;
}
